// Cow PNG from the planner icon set (resized + palette-quantized; see
// scripts/optimize_planner_icons.cjs and src/lib/plannerIcons.js). The
// existing CattleIcon API stays intact so the ~50 call sites don't have
// to migrate to PlannerIcon individually.
import type {CSSProperties, ImgHTMLAttributes, ReactNode} from 'react';

export const CATTLE_ICON_SRC = '/icons/planner/cow.png';

// `size` drives BOTH CSS dimensions, so it is a CSS length rather than the
// numeric `size` attribute; the remaining props still spread onto the <img>
// last, which is what lets a caller override src/alt if it ever needs to.
type CattleIconProps = Omit<ImgHTMLAttributes<HTMLImageElement>, 'size'> & {
  size?: CSSProperties['width'];
};

export default function CattleIcon({size = 18, style, ...props}: CattleIconProps) {
  return (
    <img
      src={CATTLE_ICON_SRC}
      alt=""
      aria-hidden="true"
      draggable="false"
      style={{
        width: size,
        height: size,
        objectFit: 'contain',
        display: 'inline-block',
        verticalAlign: 'middle',
        flex: '0 0 auto',
        ...style,
      }}
      {...props}
    />
  );
}

type CattleIconLabelProps = {
  children?: ReactNode;
  size?: CSSProperties['width'];
  gap?: CSSProperties['gap'];
  style?: CSSProperties;
};

export function CattleIconLabel({children, size = 18, gap = 5, style}: CattleIconLabelProps) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap,
        verticalAlign: 'middle',
        ...style,
      }}
    >
      <CattleIcon size={size} />
      <span>{children}</span>
    </span>
  );
}

export function renderCattleIcon(size: CSSProperties['width'] = 18, style?: CSSProperties) {
  return <CattleIcon size={size} style={style} />;
}

export function renderCattleIconLabel(
  children: ReactNode,
  {size = 18, gap = 5, style}: Omit<CattleIconLabelProps, 'children'> = {},
) {
  return (
    <CattleIconLabel size={size} gap={gap} style={style}>
      {children}
    </CattleIconLabel>
  );
}
